import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PyramidaUiState } from '../hooks/usePyramidaState';
import { usePrmdUsd } from '../hooks/usePrmdUsd';
import { formatPrmd } from '../lib/format';

/** Ticking unix-seconds clock. */
function useNowSeconds(): number {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function formatUsdApprox(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '';
  const digits = value >= 1 ? 2 : value >= 0.01 ? 3 : 4;
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: digits })}`;
}

interface PrmdAccrualProps {
  token: PyramidaUiState['token'];
  /** Compact strip inside the lobby's GRAM-table card (rendered as a span —
   *  the whole card is already the button) vs a full card row on the lottery
   *  screen. */
  variant: 'lobby' | 'lottery';
}

/**
 * Live PRMD-reward ticker: the contract mints 1 PRMD per second since the
 * last ticket purchase and hands the whole accrual to the NEXT buyer (see
 * JettonRewards.mintJetton). This widget shows that growing reward in real
 * time — the longer nobody buys, the bigger the next buyer's PRMD bonus.
 */
export function PrmdAccrual({ token, variant }: PrmdAccrualProps) {
  const { t } = useTranslation();
  const now = useNowSeconds();
  const priceUsd = usePrmdUsd();

  // Nothing to show before the first on-chain read or once emission is over.
  if (!token.mintable || token.lastMintTime <= 0) return null;

  const remaining = Math.max(0, token.maxSupply - token.totalMinted);
  const accrued = Math.min(Math.max(0, now - token.lastMintTime), remaining);
  const usd = priceUsd !== null ? formatUsdApprox(accrued * priceUsd) : '';
  const amount = (
    <>
      +{formatPrmd(accrued)} <b>PRMD</b>
      {usd && <span className="prmd-accrual__usd"> (≈{usd})</span>}
    </>
  );

  if (variant === 'lobby') {
    return (
      <span className="prmd-accrual prmd-accrual--lobby">
        <span className="prmd-accrual__icon" aria-hidden="true">
          <img src="/prmd-wallet-logo-v2.webp" alt="" draggable={false} />
        </span>
        <span className="prmd-accrual__body">
          <span className="prmd-accrual__value" role="timer">{amount}</span>
          <span className="prmd-accrual__label">{t('prmdAccrual.lobbyLabel')}</span>
        </span>
      </span>
    );
  }

  return (
    <div className="prmd-accrual prmd-accrual--lottery">
      <span className="prmd-accrual__icon" aria-hidden="true">
        <img src="/prmd-wallet-logo-v2.webp" alt="" draggable={false} />
      </span>
      <span className="prmd-accrual__body">
        <span className="prmd-accrual__value" role="timer">{amount}</span>
        <span className="prmd-accrual__label">{t('prmdAccrual.lotteryLabel')}</span>
        <span className="prmd-accrual__sub">{t('prmdAccrual.hint')}</span>
      </span>
    </div>
  );
}
